// src/services/resumo.service.ts
import { ResumoData } from '../components/resumo/types';

class ResumoService {
    private getAuthHeaders() {
        const token = localStorage.getItem('accessToken');
        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }

    // ✅ CORRIGIDO: URL base correta
    async getResumo(deckId: string, subdeckId: string, isCustomDeck: boolean = false): Promise<any> {
        const baseUrl = isCustomDeck
            ? `/api/custom-decks`
            : `/api/decks`;

        const response = await fetch(
            `${baseUrl}/${deckId}/subdecks/${subdeckId}/resumo?isCustomDeck=${isCustomDeck}`,
            {
                headers: this.getAuthHeaders(),
            }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao buscar resumo');
        }

        return response.json();
    }

    // ✅ CORRIGIDO: Salvar resumo
    async saveResumo(
        deckId: string,
        subdeckId: string,
        resumoData: Partial<ResumoData>,
        isCustomDeck: boolean = false
    ): Promise<any> {
        // Bypass Vercel proxy (15s timeout) for large saves — call Railway directly
        const apiBase = process.env.REACT_APP_API_URL || '';
        const baseUrl = isCustomDeck
            ? `${apiBase}/api/custom-decks`
            : `${apiBase}/api/decks`;

        const response = await fetch(
            `${baseUrl}/${deckId}/subdecks/${subdeckId}/resumo`,
            {
                method: 'PUT',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({
                    ...resumoData,
                    isCustomDeck,
                }),
            }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao salvar resumo');
        }

        return response.json();
    }

    // ========== PROGRESSO DE LEITURA POR PÁGINA ==========
    // Não suportado ainda pra isCustomDeck (rota só existe em /api/decks) —
    // ResumoReadPage já evita chamar isso quando isCustomDeck é true.
    async getReadProgress(deckId: string, subdeckId: string): Promise<{ pagesRead: number[]; lastPageIndex: number }> {
        const response = await fetch(
            `/api/decks/${deckId}/subdecks/${subdeckId}/resumo/progress`,
            { headers: this.getAuthHeaders() }
        );
        if (!response.ok) return { pagesRead: [], lastPageIndex: 0 };
        return response.json();
    }

    async markPageRead(
        deckId: string,
        subdeckId: string,
        pageIndex: number
    ): Promise<{ pagesRead: number[]; lastPageIndex: number; totalPages: number; readPercentage: number } | null> {
        const response = await fetch(
            `/api/decks/${deckId}/subdecks/${subdeckId}/resumo/progress`,
            {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify({ pageIndex }),
            }
        );
        if (!response.ok) return null;
        return response.json();
    }

    // ✅ CORRIGIDO: Marcar como lido
    async toggleMarkAsRead(deckId: string, subdeckId: string, isCustomDeck: boolean = false): Promise<any> {
        const baseUrl = isCustomDeck
            ? `/api/custom-decks`
            : `/api/decks`;

        const response = await fetch(
            `${baseUrl}/${deckId}/subdecks/${subdeckId}/resumo/mark-read`,
            {
                method: 'POST',
                headers: this.getAuthHeaders(),
            }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao marcar como lido');
        }

        return response.json();
    }

    // ✅ CORRIGIDO: Deletar resumo
    async deleteResumo(deckId: string, subdeckId: string, isCustomDeck: boolean = false): Promise<any> {
        const baseUrl = isCustomDeck
            ? `/api/custom-decks`
            : `/api/decks`;

        const response = await fetch(
            `${baseUrl}/${deckId}/subdecks/${subdeckId}/resumo?isCustomDeck=${isCustomDeck}`,
            {
                method: 'DELETE',
                headers: this.getAuthHeaders(),
            }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao deletar resumo');
        }

        return response.json();
    }

    // Buscar cards do subdeck
    async getSubdeckCards(deckId: string, subdeckId: string, isCustomDeck: boolean = false): Promise<any[]> {
        const baseUrl = isCustomDeck ? '/api/custom-decks' : '/api/decks';

        // Para custom decks mantém a rota antiga (não tem rota leve ainda)
        if (isCustomDeck) {
            const response = await fetch(
                `${baseUrl}/${deckId}/subdecks/${subdeckId}`,
                { headers: this.getAuthHeaders() }
            );
            if (!response.ok) return [];
            const data = await response.json();
            return data.subdeck?.cards || [];
        }

        // Para decks normais usa rota leve
        const response = await fetch(
            `${baseUrl}/${deckId}/subdecks/${subdeckId}/cards-summary`,
            { headers: this.getAuthHeaders() }
        );
        if (!response.ok) return [];
        const data = await response.json();
        return data.cards || [];
    }

    async uploadMedia(file: File): Promise<string> {
        const formData = new FormData();
        formData.append('file', file);

        const token = localStorage.getItem('accessToken');

        const response = await fetch('/api/upload-image', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Erro ao fazer upload da imagem');
        }

        const data = await response.json();
        return data.url; // ✅ Retorna URL do Cloudinary
    }


    // ========== TOGGLE BLOCKED ==========
    async toggleBlocked(
        deckId: string,
        subdeckId: string,
        isCustomDeck: boolean = false
    ): Promise<{ isBlocked: boolean; message: string }> {
        const baseUrl = isCustomDeck ? `/api/custom-decks` : `/api/decks`;

        const response = await fetch(
            `${baseUrl}/${deckId}/subdecks/${subdeckId}/resumo/toggle-blocked`,
            {
                method: 'PATCH',
                headers: this.getAuthHeaders(),  // ← usa o método da classe
            }
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao alterar status');
        }

        return response.json();
    }

    // ========== EXPORT APOSTILA ==========
    // Exporta a apostila como arquivo .graia (JSON cifrado/protegido)
    exportApostila(resumo: any, pages: any[], deckName: string, subdeckName: string): void {
        const exportData = {
            __version: '1.0',
            __type: 'graia-apostila',
            __exportedAt: new Date().toISOString(),
            __deckName: deckName,
            __subdeckName: subdeckName,
            title: resumo.title,
            pages: pages,
            blocks: resumo.blocks || [],
            quizzes: resumo.quizzes || {},
            metadata: {
                deckName,
                subdeckName,
                originalDeckId: resumo.metadata?.deckId,
                originalSubdeckId: resumo.metadata?.subdeckId,
                lastEdited: resumo.metadata?.lastEdited,
                version: resumo.metadata?.version || 1
            }
        };

        // Serializa e faz download
        const json = JSON.stringify(exportData, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const sanitizedName = `${deckName}-${subdeckName}`
            .replace(/[^a-z0-9\-_]/gi, '_')
            .toLowerCase();

        const a = document.createElement('a');
        a.href = url;
        a.download = `apostila_${sanitizedName}_${Date.now()}.graia`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    // ========== IMPORT APOSTILA ==========
    // Lê um arquivo .graia e retorna os dados parseados
    async importApostila(file: File): Promise<{
        title: string;
        pages: any[];
        blocks: any[];
        quizzes: Record<string, any>;
        metadata: any;
        __deckName: string;
        __subdeckName: string;
    }> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const text = e.target?.result as string;
                    const data = JSON.parse(text);

                    // Validação básica
                    if (data.__type !== 'graia-apostila') {
                        reject(new Error('Arquivo inválido: não é uma apostila GRAIA'));
                        return;
                    }

                    if (!data.pages || !Array.isArray(data.pages)) {
                        reject(new Error('Arquivo corrompido: páginas inválidas'));
                        return;
                    }

                    resolve(data);
                } catch {
                    reject(new Error('Erro ao ler arquivo. Verifique se é um arquivo .graia válido'));
                }
            };

            reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
            reader.readAsText(file);
        });
    }
}

export const resumoService = new ResumoService();